import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { SERVICE_CARDS, HOME_PATHS } from '../config/landings';
import { Instagram, Mail, Heart, HelpCircle, Cookie, FileText, Send, Loader2, CheckCircle } from 'lucide-react';
import { LegalModal } from './LegalModal';
import { faqData, legalNoticeData, cookiePolicyData } from '../data/legalContent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const F_COPY = {
  es: { news: 'Recibe recursos gratis de español', subscribe: 'Suscribirme', quick: 'Enlaces Rápidos',
        levels: 'Niveles', test: 'Test de nivel gratis', info: 'Información', faq: 'Preguntas Frecuentes',
        cookies: 'Política de Cookies', legal: 'Aviso Legal',
        newsSub: 'Suscríbete y recibe materiales, consejos y novedades de la academia.',
        mail: 'tu@email.com',
        brand: 'Clases de español online personalizadas con Cecilia Mateo García, examinadora DELE certificada con más de 10 años de experiencia.' },
  de: { news: 'Kostenlose Spanisch-Materialien erhalten', subscribe: 'Abonnieren', quick: 'Schnelllinks',
        levels: 'Niveaus', test: 'Kostenloser Einstufungstest', info: 'Informationen', faq: 'Häufige Fragen',
        cookies: 'Cookie-Richtlinie', legal: 'Impressum',
        newsSub: 'Abonniere und erhalte Materialien, Tipps und Neuigkeiten.',
        mail: 'deine@email.de',
        brand: 'Individueller Spanischunterricht online mit Cecilia Mateo García, akkreditierte DELE-Prüferin mit über 10 Jahren Erfahrung.' },
  en: { news: 'Get free Spanish resources', subscribe: 'Subscribe', quick: 'Quick Links',
        levels: 'Levels', test: 'Free level test', info: 'Information', faq: 'Frequently Asked Questions',
        cookies: 'Cookie Policy', legal: 'Legal Notice',
        newsSub: 'Subscribe and get materials, tips and news.',
        mail: 'your@email.com',
        brand: 'Personalised online Spanish lessons with Cecilia Mateo García, accredited DELE examiner with over 10 years of experience.' },
};

export const Footer = () => {
  const { t, language } = useLanguage();
  const f = F_COPY[language] || F_COPY.es;
  const navigate = useNavigate();
  const services = SERVICE_CARDS[language] || SERVICE_CARDS.es;
  const [modalType, setModalType] = useState(null);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [subMessage, setSubMessage] = useState('¡Gracias por suscribirte!');
  const [subError, setSubError] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubError('');
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setSubError('Introduce un email válido.');
      return;
    }
    setSubscribing(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, { email: email.trim() });
      setSubMessage(data.already ? '¡Ya estabas suscrito!' : '¡Gracias por suscribirte!');
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 5000);
    } catch (err) {
      setSubError('Hubo un error. Inténtalo de nuevo.');
    } finally {
      setSubscribing(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    const langHome = HOME_PATHS[language] || '/';
    navigate(`${langHome === '/' ? '' : langHome}/#${id}`);
  };

  return (
    <footer id="footer" className="bg-gradient-to-br from-[#7DBDD9] via-[#A8D8EA] to-[#C8E7F2] text-[#4A4A4A] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        {/* Newsletter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 mb-12 shadow-lg"
          data-testid="newsletter-box"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold text-[#4A4A4A] mb-2">{f.news}</h3>
              <p className="text-[#4A4A4A]/70">{f.newsSub}</p>
            </div>
            {subscribed ? (
              <div className="flex items-center gap-2 text-green-600 font-semibold" data-testid="newsletter-success">
                <CheckCircle className="w-6 h-6" />
                <span>{subMessage}</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} noValidate className="w-full md:w-auto">
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="newsletter-email-input"
                    placeholder={f.mail}
                    className="px-5 py-3 rounded-xl border border-white/60 bg-white focus:border-[#7DBDD9] focus:ring-2 focus:ring-[#7DBDD9]/30 outline-none min-w-[260px]"
                  />
                  <button
                    type="submit"
                    disabled={subscribing}
                    data-testid="newsletter-submit-btn"
                    className="px-6 py-3 bg-[#F5A6A4] text-white rounded-xl font-bold shadow-md hover:bg-[#E38987] transition-colors disabled:opacity-60 flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    {subscribing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    <span>{f.subscribe}</span>
                  </button>
                </div>
                {subError && <p className="text-red-600 text-sm mt-2" data-testid="newsletter-error">{subError}</p>}
              </form>
            )}
          </div>
        </motion.div>

        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="md:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center space-x-3 mb-4"
            >
              <img src="/logo.png" alt="Spanisch en la red" className="h-16 w-16 rounded-full object-cover shadow-lg border-2 border-white" />
              <h3 className="text-2xl font-bold" style={{ color: '#F5A6A4' }}>
                SPANISCH EN LA RED
              </h3>
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-[#4A4A4A]/80 mb-6 max-w-md leading-relaxed"
            >
              {f.brand}
            </motion.p>
            
            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex space-x-4"
            >
              <a
                href="https://www.instagram.com/spanischenlared/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-lg"
                data-testid="footer-instagram-link"
              >
                <Instagram className="w-6 h-6 text-[#F5A6A4]" />
              </a>
              <a
                href="mailto:spanischenlared@gmail.com"
                className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-lg"
                data-testid="footer-email-link"
              >
                <Mail className="w-6 h-6 text-[#7DBDD9]" />
              </a>
            </motion.div>
          </div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <h4 className="text-lg font-bold mb-4 text-[#4A4A4A]">{f.quick}</h4>
            <ul className="space-y-2">
              {[
                { label: t.nav.home, id: 'hero' },
                { label: t.nav.about, id: 'about' },
                { label: t.nav.methodology, id: 'methodology' },
                { label: f.levels, id: 'niveles' },
                { label: t.nav.resources, id: 'resources' },
                { label: t.nav.testimonials, id: 'testimonials' }
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-[#4A4A4A]/80 hover:text-white transition-colors duration-200 text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
              {language === 'es' && (
              <li>
                <a
                  href="/test-de-nivel"
                  data-testid="footer-test-de-nivel"
                  className="text-[#4A4A4A]/80 hover:text-white transition-colors duration-200 text-left inline-block font-semibold"
                >
                  {f.test}
                </a>
              </li>
              )}
            </ul>

            <h4 className="text-lg font-bold mt-7 mb-4 text-[#4A4A4A]">{services.title}</h4>
            <ul className="space-y-2">
              {services.cards.map((card, i) => (
                <li key={card.path}>
                  <Link
                    to={card.path}
                    data-testid={`footer-service-${i}`}
                    className="text-[#4A4A4A]/80 hover:text-white transition-colors duration-200 text-left inline-block"
                  >
                    {card.title}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Information & Legal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <h4 className="text-lg font-bold mb-4 text-[#4A4A4A]">{f.info}</h4>
            <ul className="space-y-3 text-[#4A4A4A]/80">
              <li>
                <button
                  onClick={() => setModalType('faq')}
                  className="flex items-center space-x-2 hover:text-white transition-colors"
                  data-testid="footer-faq-btn"
                >
                  <HelpCircle className="w-4 h-4" />
                  <span>{f.faq}</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => setModalType('cookies')}
                  className="flex items-center space-x-2 hover:text-white transition-colors"
                  data-testid="footer-cookies-btn"
                >
                  <Cookie className="w-4 h-4" />
                  <span>{f.cookies}</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => setModalType('legal')}
                  className="flex items-center space-x-2 hover:text-white transition-colors"
                  data-testid="footer-legal-btn"
                >
                  <FileText className="w-4 h-4" />
                  <span>{f.legal}</span>
                </button>
              </li>
              <li className="pt-2">
                <a href="mailto:spanischenlared@gmail.com" className="hover:text-white transition-colors text-sm">
                  spanischenlared@gmail.com
                </a>
              </li>
              <li>
                <a href="https://www.instagram.com/spanischenlared/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors text-sm">
                  @spanischenlared
                </a>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/30 my-8" />

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"
        >
          <p className="text-[#4A4A4A]/80 text-sm flex items-center">
            © {new Date().getFullYear()} SPANISCH EN LA RED. {t.footer.rights}
            <Heart className="w-4 h-4 text-[#F5A6A4] mx-2 fill-[#F5A6A4]" />
          </p>
          <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
            <button onClick={() => setModalType('faq')} className="text-[#4A4A4A]/80 hover:text-white transition-colors">
              {f.faq}
            </button>
            <button onClick={() => setModalType('cookies')} className="text-[#4A4A4A]/80 hover:text-white transition-colors">
              {t.footer.privacy}
            </button>
            <button onClick={() => setModalType('legal')} className="text-[#4A4A4A]/80 hover:text-white transition-colors">
              {t.footer.legal}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Legal Modal */}
      <LegalModal
        isOpen={modalType !== null}
        onClose={() => setModalType(null)}
        type={modalType}
        faqData={faqData}
        legalNoticeData={legalNoticeData}
        cookiePolicyData={cookiePolicyData}
      />
    </footer>
  );
};
